import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Popover,
  TextField,
  List,
  ListItem,
  Typography,
  Grow,
  InputAdornment,
  CircularProgress,
  styled,
} from "@mui/material";
import { ArrowDropDown, ArrowDropUp } from "@material-ui/icons";

const SingleSelectInputGrouped = ({
  label,
  placeholder,
  value = null,
  options = [],
  optionKey = "id",
  optionLabel = "label",
  onChange,
  inputValue = "",
  onInputChange,
  loading = false,
  width,
  disabled,
}) => {
  const inputRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [paperWidth, setPaperWidth] = useState(null);

  const handleOpen = e => !disabled && setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSelect = option => {
    onChange(option);
    handleClose();
  };

  const handleSearchChange = event => {
    onInputChange?.(event.target.value);
  };

  useEffect(() => {
    if (inputRef.current) {
      setPaperWidth(inputRef.current.offsetWidth);
    }
  }, [anchorEl]);
  return (
    <Box>
      <StyledTextField
        ref={inputRef}
        label={label}
        fullWidth
        customWidth={width}
        variant="outlined"
        placeholder={placeholder}
        value={value?.[optionLabel] || ""}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              {anchorEl ? <ArrowDropUp /> : <ArrowDropDown />}
            </InputAdornment>
          ),
        }}
        onClick={handleOpen}
        disabled={disabled}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        TransitionComponent={Grow}
      >
        <SelectPaper customWidth={paperWidth}>
          <SearchInput
            placeholder="Search..."
            fullWidth
            value={inputValue}
            onChange={handleSearchChange}
            autoFocus
            size="small"
          />

          {loading ? (
            <Box p={2} textAlign="center">
              <CircularProgress size={20} />
            </Box>
          ) : (
            <Box>
              {options.length > 0 ? (
                <List dense disablePadding>
                  {options.map(option => {
                    const isSelected =
                      value?.[optionKey] === option?.[optionKey];
                    return (
                      <CustomListItem
                        key={option[optionKey]}
                        selected={isSelected}
                        onClick={() => handleSelect(option)}
                      >
                        <Typography>{option[optionLabel]}</Typography>
                      </CustomListItem>
                    );
                  })}
                </List>
              ) : (
                <Box p={2}>
                  <Typography align="center" fontStyle="italic">
                    No results found
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </SelectPaper>
      </Popover>
    </Box>
  );
};

const StyledTextField = styled(TextField)(({ theme }) => ({
  width: "100%",
  "& .MuiInputBase-root": {
    height: 40,
    paddingRight: 8,
    paddingLeft: 8,
  },
  "& .MuiInputBase-input": {
    padding: "10px 0",
  },
  "& .MuiInputLabel-root": {
    top: -7,
    color: "black",
  },
  "& .MuiInputLabel-shrink": {
    top: 0,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.grey[400],
  },
}));

const SelectPaper = styled(Box)(({ customWidth }) => ({
  minWidth: customWidth,
  maxHeight: 300,
  overflowY: "auto",
}));

const CustomListItem = styled(ListItem)(() => ({
  cursor: "pointer",
  padding: "8px 16px",
}));

const SearchInput = styled(TextField)(() => ({
  padding: "8px 16px",
}));

export default SingleSelectInputGrouped;
